import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ClipboardCheck,
  Loader2,
  AlertTriangle,
  ExternalLink,
  CheckCheck,
  ChevronDown,
  Filter,
} from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  useSourceReviewMatrix,
  useResolveSourceReviewMatrix,
  useReviewVariantGroup,
} from "@/hooks/useReview";
import { getAgentPrompt } from "@/services/validationApi";
import type { ReviewStatus, MatrixCell } from "@/types/review";
import { compareProducts, compareColumns } from "@/utils/reviewMatrixSort";

interface ReviewMatrixProps {
  source: string;
  reviewerId: string;
  onChanged: () => void;
  onCellClick?: (cell: MatrixCell) => void;
}

function statusColorClass(status: string | null): string {
  const s = status as ReviewStatus | null;
  if (s === "reviewed") return "bg-[#10B981] border-[#10B981] text-white";
  if (s === "stale") return "bg-[#F59E0B] border-[#F59E0B] text-white";
  if (s === "in_progress") return "bg-[#3B82F6] border-[#3B82F6] text-white";
  if (s === "canceled") return "bg-[#94A3B8] border-[#94A3B8] text-white";
  return "bg-[#6B7280] border-[#6B7280] text-white";
}

function statusLabel(status: string | null): string {
  if (!status) return "unreviewed";
  if (status === "in_progress") return "in progress";
  return status;
}

const MATRIX_STATUS_OPTIONS = [
  { value: "unreviewed", label: "Unreviewed" },
  { value: "stale", label: "Stale" },
  { value: "in_progress", label: "In progress" },
  { value: "reviewed", label: "Reviewed" },
  { value: "canceled", label: "Canceled" },
];

export function ReviewMatrix({
  source,
  reviewerId,
  onChanged,
  onCellClick,
}: ReviewMatrixProps) {
  const { data, isLoading, error, refetch } = useSourceReviewMatrix(source);
  const resolveMatrix = useResolveSourceReviewMatrix();
  const reviewVariantGroup = useReviewVariantGroup();

  const [reviewingDiagonal, setReviewingDiagonal] = useState(false);
  const [resolvingMatrix, setResolvingMatrix] = useState(false);
  const [copyingPromptKey, setCopyingPromptKey] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set());
  const [typeFilter, setTypeFilter] = useState<
    "all" | "direct" | "indirect" | "none"
  >("all");
  const [needsReviewOnly, setNeedsReviewOnly] = useState(false);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <p className="text-center py-12 text-destructive">
        Failed to load matrix.{" "}
        <button className="underline" onClick={() => refetch()}>
          Retry
        </button>
      </p>
    );
  }

  const { products, columns, cells, plan } = data;

  const sortedProducts = [...products].sort(compareProducts);
  const sortedColumns = [...columns].sort(compareColumns);

  if (products.length === 0 || columns.length === 0) {
    return (
      <p className="text-center py-12 text-muted-foreground">
        No matrix data for this source.
      </p>
    );
  }

  // Build lookup: "{product_id}|{grade_name ?? ""}|{fingerprint}" -> MatrixCell
  const cellMap = new Map<string, MatrixCell>();
  for (const cell of cells) {
    const key = `${cell.product_id}|${cell.grade_name ?? ""}|${cell.fingerprint}`;
    cellMap.set(key, cell);
  }

  // Build plan set: "{product_id}|{grade_name ?? ""}"
  const planSet = new Set<string>(
    plan.cells_to_review.map((c) => `${c.product_id}|${c.grade_name ?? ""}`),
  );

  const isFiltering =
    statusFilter.size > 0 || typeFilter !== "all" || needsReviewOnly;

  const matchesFilter = (cell: MatrixCell): boolean => {
    if (
      statusFilter.size > 0 &&
      statusFilter.size < MATRIX_STATUS_OPTIONS.length
    ) {
      const effectiveStatus = cell.review_status ?? "unreviewed";
      if (!statusFilter.has(effectiveStatus)) return false;
    }
    if (typeFilter === "direct" && cell.review_type !== "direct") return false;
    if (typeFilter === "indirect" && cell.review_type !== "indirect")
      return false;
    if (typeFilter === "none" && cell.review_type !== null) return false;
    if (needsReviewOnly) {
      const isPlanned = planSet.has(
        `${cell.product_id}|${cell.grade_name ?? ""}`,
      );
      if (!isPlanned) return false;
      if (cell.review_status === "reviewed" && cell.review_type === "direct")
        return false;
    }
    return true;
  };

  const filteredCellKeys: Set<string> | null = isFiltering
    ? new Set(
        cells
          .filter(matchesFilter)
          .map((c) => `${c.product_id}|${c.grade_name ?? ""}|${c.fingerprint}`),
      )
    : null;

  const activeColumns = filteredCellKeys
    ? sortedColumns.filter((col) =>
        sortedProducts.some((p) =>
          filteredCellKeys.has(
            `${p.product_id}|${col.grade_name ?? ""}|${col.fingerprint}`,
          ),
        ),
      )
    : sortedColumns;

  const activeProducts = filteredCellKeys
    ? sortedProducts.filter((p) =>
        sortedColumns.some((col) =>
          filteredCellKeys.has(
            `${p.product_id}|${col.grade_name ?? ""}|${col.fingerprint}`,
          ),
        ),
      )
    : sortedProducts;

  const handleReviewDiagonal = async () => {
    setReviewingDiagonal(true);
    let reviewed = 0;
    let skipped = 0;
    try {
      for (const planCell of plan.cells_to_review) {
        // Find the matching MatrixCell (same product_id + grade_name)
        const matchingCell = cells.find(
          (c) =>
            c.product_id === planCell.product_id &&
            c.grade_name === planCell.grade_name,
        );
        if (!matchingCell) {
          skipped++;
          continue;
        }
        // Skip if already directly reviewed
        if (
          matchingCell.review_status === "reviewed" &&
          matchingCell.review_type === "direct"
        ) {
          skipped++;
          continue;
        }
        // Skip fingerprint conflicts
        if (matchingCell.fingerprint_conflict) {
          skipped++;
          continue;
        }
        const representativeVariantId = matchingCell.variant_ids[0];
        if (representativeVariantId == null) {
          skipped++;
          continue;
        }
        await reviewVariantGroup.mutateAsync({
          variantId: representativeVariantId,
          body: {
            status: "reviewed",
            reviewer_type: "human",
            reviewer_identifier: reviewerId,
            review_type: "direct",
            notes: "Matrix diagonal review",
          },
        });
        reviewed++;
      }
      if (reviewed > 0) {
        toast.success(
          `Reviewed ${reviewed} diagonal cell${reviewed !== 1 ? "s" : ""}${skipped > 0 ? ` (${skipped} skipped)` : ""}.`,
        );
      } else {
        toast.info(
          `No cells to review (${skipped} already reviewed or conflicted).`,
        );
      }
      refetch();
      onChanged();
    } catch (e) {
      toast.error(
        `Diagonal review failed: ${e instanceof Error ? e.message : String(e)}`,
      );
    } finally {
      setReviewingDiagonal(false);
    }
  };

  const handleResolveMatrix = async () => {
    setResolvingMatrix(true);
    try {
      const result = await resolveMatrix.mutateAsync({
        sourceId: source,
        body: {
          reviewer_type: "human",
          reviewer_identifier: reviewerId,
          notes: "Matrix diagonal resolve",
        },
      });
      const uncoveredCount = result.uncovered.length;
      if (uncoveredCount > 0) {
        const reasons = result.uncovered
          .slice(0, 3)
          .map(
            (u) =>
              `Product ${u.product_id}/${u.grade_name ?? "null"}: ${u.reason}`,
          )
          .join("; ");
        toast.warning(
          `Resolved: ${result.directly_reviewed} direct, ${result.indirectly_covered} indirect. ${uncoveredCount} uncovered: ${reasons}${uncoveredCount > 3 ? "…" : ""}`,
          { duration: 8000 },
        );
      } else {
        toast.success(
          `Matrix resolved: ${result.directly_reviewed} direct, ${result.indirectly_covered} indirectly covered.`,
        );
      }
      refetch();
      onChanged();
    } catch (e) {
      toast.error(
        `Resolve failed: ${e instanceof Error ? e.message : String(e)}`,
      );
    } finally {
      setResolvingMatrix(false);
    }
  };

  const handleCopyCellPrompt = async (cell: MatrixCell) => {
    const variantId = cell.variant_ids[0];
    if (variantId == null) {
      toast.error("No variants available for a prompt");
      return;
    }
    setCopyingPromptKey(`${cell.product_id}|${cell.fingerprint}`);
    try {
      const text = await getAgentPrompt({
        product_id: cell.product_id,
        variant_id: variantId,
        use_group_review: true,
      });
      await navigator.clipboard.writeText(text);
      toast.success("Prompt copied to clipboard");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to fetch prompt");
    } finally {
      setCopyingPromptKey(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Summary */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Matrix plan summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 text-sm mb-4">
            <span>
              <span className="font-medium">{plan.planned_count}</span> cells to
              review
            </span>
            <span>
              <span className="font-medium">{plan.total_cells}</span> total
              cells
            </span>
            <span>
              <span className="font-medium">{plan.savings}</span> saved vs
              direct
            </span>
            <span className="text-muted-foreground">
              (full cartesian: {plan.full_cartesian_count})
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={handleReviewDiagonal}
              disabled={reviewingDiagonal || resolvingMatrix}
            >
              {reviewingDiagonal ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCheck className="w-4 h-4 mr-2" />
              )}
              Review diagonal ({plan.planned_count} cells)
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleResolveMatrix}
              disabled={resolvingMatrix || reviewingDiagonal}
            >
              {resolvingMatrix ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : null}
              Resolve matrix
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Status multi-select */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-8 gap-1">
              <Filter className="w-3 h-3" />
              {statusFilter.size === 0 ||
              statusFilter.size === MATRIX_STATUS_OPTIONS.length
                ? "Status"
                : `Status (${statusFilter.size})`}
              <ChevronDown className="w-3 h-3 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-2" align="start">
            <div className="flex flex-col gap-1">
              {MATRIX_STATUS_OPTIONS.map((opt) => (
                <label
                  key={opt.value}
                  className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground"
                >
                  <Checkbox
                    checked={statusFilter.has(opt.value)}
                    onCheckedChange={(checked) =>
                      setStatusFilter((prev) => {
                        const next = new Set(prev);
                        if (checked) next.add(opt.value);
                        else next.delete(opt.value);
                        return next;
                      })
                    }
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        {/* Review type select */}
        <Select
          value={typeFilter}
          onValueChange={(v) => setTypeFilter(v as typeof typeFilter)}
        >
          <SelectTrigger className="h-8 w-36 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="direct">Direct</SelectItem>
            <SelectItem value="indirect">Indirect</SelectItem>
            <SelectItem value="none">No type</SelectItem>
          </SelectContent>
        </Select>

        {/* Needs matrix review toggle */}
        <Button
          size="sm"
          variant={needsReviewOnly ? "default" : "outline"}
          className="h-8"
          onClick={() => setNeedsReviewOnly((prev) => !prev)}
        >
          Needs matrix review
        </Button>

        {/* Clear all */}
        {isFiltering && (
          <Button
            size="sm"
            variant="ghost"
            className="h-8"
            onClick={() => {
              setStatusFilter(new Set());
              setTypeFilter("all");
              setNeedsReviewOnly(false);
            }}
          >
            Clear
          </Button>
        )}
      </div>

      {/* Grid */}
      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[160px] sticky left-0 bg-card z-10">
                Product
              </TableHead>
              {activeColumns.map((col) => (
                <TableHead
                  key={`${col.grade_name ?? ""}|${col.fingerprint}`}
                  className="min-w-[120px] text-center"
                >
                  <div className="flex flex-col items-center gap-0.5">
                    <span>{col.grade_name ?? "(no grade)"}</span>
                    <span className="text-xs text-muted-foreground font-mono">
                      {col.fingerprint.slice(0, 7)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {col.product_count}p
                    </span>
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {activeProducts.map((product) => (
              <TableRow key={product.product_id}>
                <TableCell className="sticky left-0 bg-card z-10 font-medium text-sm">
                  <div className="flex flex-col gap-0.5">
                    <span>
                      {product.product_name ?? `Product ${product.product_id}`}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      #{product.product_id}
                    </span>
                  </div>
                </TableCell>
                {activeColumns.map((col) => {
                  const cellKey = `${product.product_id}|${col.grade_name ?? ""}|${col.fingerprint}`;
                  const cell = cellMap.get(cellKey);
                  const planKey = `${product.product_id}|${col.grade_name ?? ""}`;
                  const isPlanned = planSet.has(planKey);

                  if (
                    !cell ||
                    (filteredCellKeys && !filteredCellKeys.has(cellKey))
                  ) {
                    return (
                      <TableCell
                        key={cellKey}
                        className="text-center text-muted-foreground text-xs"
                      >
                        —
                      </TableCell>
                    );
                  }

                  const representativeVariantId = cell.variant_ids[0];

                  return (
                    <TableCell
                      key={cellKey}
                      className={cn(
                        "text-center p-2",
                        isPlanned && "ring-2 ring-inset ring-primary",
                        onCellClick &&
                          "cursor-pointer hover:bg-muted/50 transition-colors",
                      )}
                      onClick={() => onCellClick?.(cell)}
                      title={onCellClick ? "View variants in queue" : undefined}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <Badge
                          variant="outline"
                          className={cn(
                            "uppercase text-xs",
                            statusColorClass(cell.review_status),
                          )}
                        >
                          {statusLabel(cell.review_status)}
                        </Badge>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          {cell.review_type && (
                            <span className="capitalize">
                              {cell.review_type}
                            </span>
                          )}
                          <span>{cell.variant_count}v</span>
                          {cell.fingerprint_conflict && (
                            <span title="Fingerprint conflict">
                              <AlertTriangle className="w-3 h-3 text-orange-500" />
                            </span>
                          )}
                        </div>
                        {representativeVariantId != null && (
                          <div className="flex items-center gap-1">
                            <a
                              href={`/product-viewer?type=variant&id=${representativeVariantId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-muted-foreground hover:text-foreground"
                              title="Open in Product Viewer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="w-3 h-3" />
                            </a>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-5 w-5 text-xs text-muted-foreground hover:text-foreground"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyCellPrompt(cell);
                              }}
                              disabled={
                                copyingPromptKey ===
                                `${cell.product_id}|${cell.fingerprint}`
                              }
                              title="Copy prompt for this variant"
                            >
                              {copyingPromptKey ===
                              `${cell.product_id}|${cell.fingerprint}` ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <ClipboardCheck className="w-3 h-3" />
                              )}
                            </Button>
                          </div>
                        )}
                      </div>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
